import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Crown, CheckCircle2, Loader2, XCircle } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

export default function DonationSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | expired | error
  const [data, setData] = useState(null);

  const poll = useCallback(
    async (attempt = 0) => {
      if (!sessionId) {
        setStatus("error");
        return;
      }
      if (attempt >= 10) {
        setStatus("error");
        return;
      }
      try {
        const res = await axios.get(`${API}/donations/status/${sessionId}`);
        setData(res.data);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (res.data.status === "expired") {
          setStatus("expired");
          return;
        }
        setTimeout(() => poll(attempt + 1), 2000);
      } catch {
        setTimeout(() => poll(attempt + 1), 2500);
      }
    },
    [sessionId]
  );

  useEffect(() => {
    poll(0);
  }, [poll]);

  return (
    <main
      data-testid="donation-success-page"
      className="min-h-screen bg-[#FAF7F2] text-[#2C1E23] flex flex-col"
    >
      <header className="max-w-[1200px] mx-auto w-full px-6 py-8 flex items-center gap-3">
        <span className="w-10 h-10 border border-[#4A192C] flex items-center justify-center">
          <Crown className="w-5 h-5 text-[#4A192C]" />
        </span>
        <span className="font-serif-display text-xl text-[#4A192C]">
          Le Sanctuaire des Mères
        </span>
      </header>

      <div className="flex-1 flex items-center">
        <div className="max-w-[720px] mx-auto px-6 py-20 text-center">
          {status === "polling" && (
            <div data-testid="donation-polling">
              <Loader2 className="w-12 h-12 text-[#C5A059] mx-auto animate-spin" />
              <h1 className="font-serif-display text-4xl text-[#4A192C] mt-8">
                Vérification de votre don...
              </h1>
              <p className="mt-4 text-[#6B585F]">
                Merci de patienter quelques instants.
              </p>
            </div>
          )}

          {status === "paid" && (
            <div data-testid="donation-paid">
              <CheckCircle2 className="w-14 h-14 text-[#C5A059] mx-auto" />
              <p className="sdm-divider mt-8">Don reçu avec gratitude</p>
              <h1 className="font-serif-display text-5xl lg:text-6xl text-[#4A192C] mt-6 leading-tight">
                Merci, <em className="text-[#C5A059]">Reine.</em>
              </h1>
              {data && (
                <p className="mt-6 text-lg text-[#2C1E23]/80">
                  Votre don de{" "}
                  <strong>
                    {data.amount_total.toFixed(2)} {data.currency?.toUpperCase()}
                  </strong>{" "}
                  a été confirmé.
                </p>
              )}
              <p className="mt-4 text-[#6B585F] max-w-lg mx-auto leading-relaxed">
                Chaque euro offert devient une nuit d&apos;accueil, un atelier
                de parole, une cape symbolique. Vous tenez, vous aussi, une
                place dans le cercle.
              </p>
              <Link
                to="/"
                data-testid="back-home-button"
                className="sdm-btn-primary mt-10 inline-flex"
              >
                Retour au Sanctuaire
              </Link>
            </div>
          )}

          {(status === "error" || status === "expired") && (
            <div data-testid="donation-error">
              <XCircle className="w-12 h-12 text-[#D98A6C] mx-auto" />
              <h1 className="font-serif-display text-4xl text-[#4A192C] mt-6">
                Le paiement n&apos;a pas pu être confirmé
              </h1>
              <p className="mt-4 text-[#6B585F]">
                Si un montant a été débité, notre équipe le vérifiera manuellement.
              </p>
              <Link to="/" className="sdm-btn-primary mt-10 inline-flex">
                Retour à l&apos;accueil
              </Link>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
